/**
 * Avatar Service
 *
 * Handles avatar upload, deletion, and URL generation.
 */

import { supabase } from '@/lib/supabase';

const BUCKET = 'images';

/**
 * Uploads an avatar image for a user.
 * Stores in images bucket under avatars/{userId}/avatar.{ext}
 * Updates the avatar_path column on profiles.
 */
export const uploadAvatar = async (userId: string, file: File): Promise<string> => {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `avatars/${userId}/avatar.${ext}`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
  if (error) throw error;

  const { error: updateError } = await supabase
    .from('profiles')
    .update({ avatar_path: path })
    .eq('id', userId);
  if (updateError) throw updateError;

  return path;
};

/**
 * Deletes a user's avatar from storage and clears the profile column.
 */
export const deleteAvatar = async (userId: string, path: string): Promise<void> => {
  await supabase.storage.from(BUCKET).remove([path]);
  await supabase.from('profiles').update({ avatar_path: null }).eq('id', userId);
};

/**
 * Returns the public URL for an avatar storage path.
 */
export const getAvatarUrl = (path: string): string => {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return data.publicUrl;
};
